import { styled } from "styled-components";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { ChangeCell, TextCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { ItemIcon, LegendBullet, ProgressBar, Type } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter, percentagePointFilter } from "app/filters/ngFilters";
import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import type { IPeriodOverPeriodCompareType } from "app/services/periodOverPeriodTypes";

import type { PlayerData, TopPlayersComparisonRow } from "./useTopPlayersComparisonData";

const marketShareFormatter = percentageSignFilter();

const StyledItemIcon = styled(ItemIcon)`
    width: 24px;
    height: 24px;
`;

const InlineHeaderCell = styled.div`
    display: inline-block;
`;

export const stripedBackground = `repeating-linear-gradient(
    -45deg,
    ${colorsPalettes.carbon[200]} 0,
    ${colorsPalettes.carbon[200]} 1px,
    transparent 1px,
    transparent 3px
)`;

const renderTitle = (category: ICategoryComparison) => (
    <InlineHeaderCell>
        <LegendBullet
            labelColor={category.color}
            text={<Text fontVariant="captionTitle">{category.category.text}</Text>}
        />
    </InlineHeaderCell>
);

const renderPlayerDomain = (playerData: PlayerData | null) => {
    return (
        <Flex
            alignItems="center"
            gap="4px"
            flex="1"
        >
            <StyledItemIcon
                iconType={Type.Website}
                iconSrc={playerData?.playerIcon}
            />
            <Text
                fontVariant="input2"
                color={colorsPalettes.carbon[400]}
            >
                {playerData?.playerName}
            </Text>
        </Flex>
    );
};

const renderComparisonTrafficShareHeaderCell = (isPreviousPeriod: boolean) => {
    return (
        <InlineHeaderCell>
            <Flex
                justifyContent="flex-end"
                alignItems="center"
                gap="4px"
            >
                <Box
                    width="8px"
                    height="8px"
                    borderRadius="50%"
                    background={isPreviousPeriod ? stripedBackground : colorsPalettes.carbon[200]}
                />
                <Text fontVariant="captionTitle">
                    {translate("market.research.overview.comparison.topPlayers.table.trafficShare")}
                </Text>
            </Flex>
        </InlineHeaderCell>
    );
};

const renderCell = (playerData: PlayerData | null) => {
    if (!playerData) {
        return (
            <Flex alignItems="center">
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon[200]}
                >
                    -
                </Text>
            </Flex>
        );
    }

    return (
        <Flex alignItems="center">
            {renderPlayerDomain(playerData)}
            <Flex
                flex="1"
                gap="12px"
                alignItems="center"
            >
                <Flex
                    width="40px"
                    justifyContent="flex-end"
                    flexShrink={0}
                >
                    <Text fontVariant="input2">{marketShareFormatter(playerData.marketShare, 1)}</Text>
                </Flex>
                <Flex flex="1">
                    <ProgressBar
                        values={[playerData.marketShare]}
                        colorSet={[playerData.color]}
                    />
                </Flex>
            </Flex>
        </Flex>
    );
};

export const getColumnConfig = (categories: ICategoryComparison[]): ColumnsType<TopPlayersComparisonRow> => [
    {
        key: "playerRank",
        title: "",
        render: (_, row) => (
            <Flex justifyContent="center">
                <TextCell>{row.playerRank}</TextCell>
            </Flex>
        ),
        width: 60,
    },
    {
        key: "primaryCategory",
        title: renderTitle(categories[0]),
        render: (_, row) => renderCell(row.primaryCategory),
    },
    {
        key: "comparedCategory",
        title: renderTitle(categories[1]),
        render: (_, row) => renderCell(row.comparedCategory),
    },
];

export const getComparisonColumnConfig = (
    categories: ICategoryComparison[],
    periodOverPeriodCompareType: IPeriodOverPeriodCompareType,
): ColumnsType<TopPlayersComparisonRow> => [
    {
        key: "playerRank",
        title: "",
        render: (_, row) => (
            <Flex justifyContent="center">
                <TextCell>{row.playerRank}</TextCell>
            </Flex>
        ),
        width: 60,
    },
    {
        key: "primaryCategory",
        title: renderTitle(categories[0]),
        align: "left",
        children: [
            {
                key: "playerName",
                align: "left",
                title: translate("common.domain"),
                render: (_, row) => renderPlayerDomain(row.primaryCategory),
                width: 180,
            },
            {
                key: "previousPeriodMarketShare",
                align: "right",
                title: renderComparisonTrafficShareHeaderCell(true),
                render: (_, row) => (
                    <TextCell>{marketShareFormatter(row.primaryCategory?.previousPeriodMarketShare, 2)}</TextCell>
                ),
                width: 100,
            },
            {
                key: "marketShare",
                align: "right",
                title: renderComparisonTrafficShareHeaderCell(false),
                render: (_, row) => <TextCell>{marketShareFormatter(row.primaryCategory?.marketShare, 2)}</TextCell>,
                width: 100,
            },
            {
                key: "change",
                align: "right",
                title: translate(
                    periodOverPeriodCompareType === "previous year"
                        ? "market.research.overview.comparison.topPlayers.table.change.yoy"
                        : "market.research.overview.comparison.topPlayers.table.change.pop",
                ),
                render: (_, row) => (
                    <ChangeCell
                        value={row.primaryCategory?.change}
                        formattedValue={
                            row.primaryCategory?.marketShare !== undefined &&
                            row.primaryCategory?.previousPeriodMarketShare !== undefined
                                ? percentagePointFilter(
                                      row.primaryCategory.marketShare,
                                      row.primaryCategory.previousPeriodMarketShare,
                                  )
                                : "-"
                        }
                    />
                ),
                width: 100,
            },
        ],
    },
    {
        key: "comparedCategory",
        title: renderTitle(categories[1]),
        align: "left",
        children: [
            {
                key: "playerName",
                align: "left",
                title: translate("common.domain"),
                render: (_, row) => renderPlayerDomain(row.comparedCategory),
                width: 180,
            },
            {
                key: "previousPeriodMarketShare",
                align: "right",
                title: renderComparisonTrafficShareHeaderCell(true),
                render: (_, row) => (
                    <TextCell>{marketShareFormatter(row.comparedCategory?.previousPeriodMarketShare, 2)}</TextCell>
                ),
                width: 100,
            },
            {
                key: "marketShare",
                align: "right",
                title: renderComparisonTrafficShareHeaderCell(false),
                render: (_, row) => <TextCell>{marketShareFormatter(row.comparedCategory?.marketShare, 2)}</TextCell>,
                width: 100,
            },
            {
                key: "change",
                align: "right",
                title: translate(
                    periodOverPeriodCompareType === "previous year"
                        ? "market.research.overview.comparison.topPlayers.table.change.yoy"
                        : "market.research.overview.comparison.topPlayers.table.change.pop",
                ),
                render: (_, row) => (
                    <ChangeCell
                        value={row.comparedCategory?.change}
                        formattedValue={
                            row.comparedCategory?.marketShare !== undefined &&
                            row.comparedCategory?.previousPeriodMarketShare !== undefined
                                ? percentagePointFilter(
                                      row.comparedCategory.marketShare,
                                      row.comparedCategory.previousPeriodMarketShare,
                                  )
                                : "-"
                        }
                    />
                ),
                width: 100,
            },
        ],
    },
];
